import { normalizeEventName } from '@trafilea/tss-lib-funnel-schema-ts';
import { isFunnelCommandSemantic } from '@trafilea/tss-lib-funnel-schema-ts';
import type { FunnelAnalytics } from '../analytics';
import type { FunnelEventBus } from './funnel-event-bus';
import type { TssEventEnvelope, UIEventPayload } from './envelope';
import {
  redactContextForTransport,
  redactParamsForTransport,
} from './transport-adapter';

/**
 * PLAIN name → GA4 / dataLayer mapping (D-G). Lookups normalize the envelope
 * name, so compatible ui.- or funnel.ui.-prefixed emissions map too.
 */
const GA4_MAP: Record<string, { event: string; paramKey?: string }> = {
  cta_clicked: { event: 'funnel_cta_click' },
  lead_captured: { event: 'funnel_lead_capture' },
  offer_accepted: { event: 'funnel_accept' },
  offer_declined: { event: 'funnel_decline' },
  experience_dismissed: { event: 'funnel_dismiss' },
  block_viewed: { event: 'funnel_block_view' },
  spin_completed: { event: 'funnel_spin_complete' },
};

export function attachAnalyticsBusAdapter(
  bus: FunnelEventBus,
  analytics: FunnelAnalytics,
): { unsubscribe: () => void } {
  return bus.on('*', (envelope: TssEventEnvelope) => {
    const normalizedName = normalizeEventName(envelope.name);
    // Executable semantic names are analytics facts only at their result
    // boundary. Observation-only names can be projected immediately.
    if (
      isFunnelCommandSemantic(normalizedName) &&
      envelope.metadata.phase !== 'result'
    ) {
      return;
    }
    const mapping = GA4_MAP[normalizedName];
    if (!mapping) return;

    const payload = envelope.payload as UIEventPayload | undefined;
    const ctx = payload?.context;
    const params = payload?.params ?? {};

    if (mapping.event === 'funnel_accept' && analytics.trackOfferAccepted) {
      analytics.trackOfferAccepted({
        funnelId: ctx?.funnelId ?? '',
        stepId: ctx?.stepId ?? '',
        offerId: params.offerId as string | undefined,
        offerName: params.offerName as string | undefined,
        productId: params.productId as string | undefined,
      });
      return;
    }
    if (mapping.event === 'funnel_decline' && analytics.trackOfferDeclined) {
      analytics.trackOfferDeclined({
        funnelId: ctx?.funnelId ?? '',
        stepId: ctx?.stepId ?? '',
        offerId: params.offerId as string | undefined,
      });
      return;
    }
    if (mapping.event === 'funnel_dismiss' && analytics.trackExperienceDismissed) {
      analytics.trackExperienceDismissed({
        funnelId: ctx?.funnelId ?? '',
        stepId: ctx?.stepId ?? '',
      });
      return;
    }

    if (typeof window !== 'undefined' && window.dataLayer) {
      // Apply PII allowlists before pushing to GA4/dataLayer (TF-AUD-013).
      const safeParams = redactParamsForTransport(params);
      const safeCtx = redactContextForTransport(ctx ?? {});
      window.dataLayer.push({
        event: mapping.event,
        funnel_event_name: envelope.name,
        ...safeParams,
        ...safeCtx,
      });
    }
  });
}

declare global {
  interface Window {
    dataLayer?: Record<string, unknown>[];
  }
}
